import { useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { X, Printer, Loader2 } from "lucide-react";
import api from "../lib/api";

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** "quotes" | "invoices" | "payments" | "expenses" | "platform-invoices" — bepaalt de backend-preview-route. */
  kind: "quotes" | "invoices" | "payments" | "expenses" | "platform-invoices";
  id: string | null;
  title?: string;
}

/**
 * Brede in-app pop-up die de server-side gegenereerde offerte- of factuur-HTML
 * toont. De HTML wordt via een geauthenticeerd axios-verzoek opgehaald (de JWT
 * zit in de interceptor, dus géén `{"error":"Unauthorized"}` meer zoals bij een
 * nieuw tabblad zonder header). Het document wordt in een sandboxed iframe
 * gerenderd zodat de Tailwind-CDN-styling van het document geïsoleerd blijft.
 */
export function PreviewModal({ isOpen, onClose, kind, id, title }: PreviewModalProps) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!isOpen || !id) return;
    setLoading(true);
    setError("");
    setHtml("");
    api
      .get(`/api/${kind}/${id}/preview`, { responseType: "text", transformResponse: (r) => r })
      .then((res) => {
        const raw = typeof res.data === "string" ? res.data : "";
        // Injecteer een <base> zodat relatieve URL's (zoals /uploads/logo.png) in het
        // about:srcdoc-iframe resolveert naar de backend, i.p.v. about:blank → geen logo.
        const base = import.meta.env.VITE_API_URL || "";
        const withBase =
          base && raw.includes("<head>")
            ? raw.replace("<head>", `<head><base href="${base.endsWith("/") ? base : base + "/"}">`)
            : raw;
        setHtml(withBase);
      })
      .catch((err: any) => {
        setError(err?.response?.data?.error || "Kon de preview niet laden.");
      })
      .finally(() => setLoading(false));
  }, [isOpen, id, kind]);

  const handlePrint = () => {
    try {
      iframeRef.current?.contentWindow?.focus();
      iframeRef.current?.contentWindow?.print();
    } catch {
      // Fallback: open het document in een nieuw tabblad.
      if (html) {
        const w = window.open("", "_blank");
        if (w) { w.document.write(html); w.document.close(); }
      }
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel
                className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all flex flex-col"
                style={{ maxHeight: "92vh", boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)" }}
              >
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white shrink-0">
                  <div>
                    <Dialog.Title className="text-lg font-bold text-slate-800">
                      {title || (kind === "quotes" ? "Offerte" : kind === "invoices" ? "Factuur" : kind === "payments" ? "Betalingsbewijs" : kind === "platform-invoices" ? "Te betalen factuur" : "Uitgave")} weergave
                    </Dialog.Title>
                    <p className="text-sm text-slate-500 mt-0.5">
                      Gebruik "Afdrukken / PDF" om het A4-document te printen of op te slaan.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrint}
                      disabled={loading || !!error || !html}
                      className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-3.5 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Printer size={20} /> Afdrukken / PDF
                    </button>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X size={30} />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden bg-slate-100/60 p-4 min-h-0">
                  {loading && (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500 py-20">
                      <Loader2 size={40} className="animate-spin mb-3" />
                      <p className="text-sm font-medium">Weergave laden...</p>
                    </div>
                  )}
                  {error && !loading && (
                    <div className="flex flex-col items-center justify-center h-full text-rose-600 py-20">
                      <p className="text-sm font-medium">{error}</p>
                    </div>
                  )}
                  {!loading && !error && html && (
                    <iframe
                      ref={iframeRef}
                      title="preview"
                      srcDoc={html}
                      sandbox="allow-popups allow-same-origin allow-modals allow-scripts"
                      className="w-full h-full bg-white rounded-xl border border-slate-200 shadow-inner"
                      style={{ minHeight: "70vh" }}
                    />
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}